import { useMemo, useCallback } from 'react'
import useRouter from './useRouter'

// Status and priority constants - exported for use elsewhere
export const ALL_STATUSES = ['OPEN', 'IN_PROGRESS', 'RESOLVED', 'VERIFIED', 'CLOSED', 'REOPENED'] as const
export const ALL_PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const

export type Status = typeof ALL_STATUSES[number]
export type Priority = typeof ALL_PRIORITIES[number]

export interface SnagsFilters {
  buildingId: string | null
  floorId: string | null
  statuses: Status[]
  priorities: Priority[]
  infrastructureTypes: string[]
  searchQuery: string
}

interface UseSnagsFiltersOptions {
  defaultInfrastructureTypes?: string[]
}

export default function useSnagsFilters(
  options: UseSnagsFiltersOptions = {}
): [SnagsFilters, (filters: SnagsFilters) => void] {
  const { history, match, query } = useRouter()
  const { defaultInfrastructureTypes = [] } = options

  // Parse filters from URL query params
  // Empty array = show all, values in array = only show those (INCLUDED)
  const filters = useMemo<SnagsFilters>(() => {
    // Parse statuses - empty array = show all
    const statusParam = query.status as string | undefined
    const statuses: Status[] = statusParam
      ? (statusParam.split(',').filter((s) => ALL_STATUSES.includes(s as Status)) as Status[])
      : [] // Empty = show all

    // Parse priorities - empty array = show all
    const priorityParam = query.priority as string | undefined
    const priorities: Priority[] = priorityParam
      ? (priorityParam.split(',').filter((p) => ALL_PRIORITIES.includes(p as Priority)) as Priority[])
      : [] // Empty = show all

    // Parse infrastructure types - empty array = show all
    const typeParam = query.type as string | undefined
    const infrastructureTypes: string[] = typeParam
      ? typeParam.split(',')
      : [] // Empty = show all

    return {
      buildingId: (query.building as string) || null,
      floorId: (query.floor as string) || null,
      statuses,
      priorities,
      infrastructureTypes,
      searchQuery: (query.q as string) || '',
    }
  }, [query])

  // Update URL with new filters
  const setFilters = useCallback(
    (newFilters: SnagsFilters) => {
      const params = new URLSearchParams()

      // Preserve tab if it exists
      if (query.tab) {
        params.set('tab', query.tab as string)
      }

      // Only add params that differ from defaults
      if (newFilters.buildingId) {
        params.set('building', newFilters.buildingId)
      }

      if (newFilters.floorId) {
        params.set('floor', newFilters.floorId)
      }

      // Add status param if any statuses are selected (empty = show all, no URL param needed)
      if (newFilters.statuses.length > 0) {
        params.set('status', newFilters.statuses.join(','))
      }

      // Add priority param if any priorities are selected (empty = show all, no URL param needed)
      if (newFilters.priorities.length > 0) {
        params.set('priority', newFilters.priorities.join(','))
      }

      // Add type param if any types are selected (empty = show all, no URL param needed)
      if (newFilters.infrastructureTypes.length > 0) {
        params.set('type', newFilters.infrastructureTypes.join(','))
      }

      if (newFilters.searchQuery) {
        params.set('q', newFilters.searchQuery)
      }

      const queryString = params.toString()
      history.replace(`${match.url}${queryString ? `?${queryString}` : ''}`)
    },
    [history, match.url, query.tab]
  )

  return [filters, setFilters]
}
